import get from 'lodash/get'
import pick from 'lodash/pick'
import moment from 'moment'
import querystring from 'query-string'
import findIndex from 'lodash/findIndex'
import differenceInDays from 'date-fns/differenceInDays'

import { priceGuidanceTopDestinations } from './price-guidance-constants'
import { translateSearchRequest } from '../../listings-client/util/translateSearchRequest'

export function getIsShelfEligible(
  searchRequest,
  hasUSDestinationsOnly,
  tripType,
  hasExpressDeal,
  cabinClass,
  browser,
  isDesktop,
  hasUpsellOptions
) {
  return (
    get(searchRequest, 'params[alt-dates]') !== 'true' &&
    hasUSDestinationsOnly &&
    tripType === 'OW' &&
    !hasExpressDeal &&
    cabinClass === 'ECO' &&
    browser !== 'ie' &&
    isDesktop &&
    hasUpsellOptions
  )
}

export function getNumDaysToDeparture(departDateString) {
  const today = new Date()
  const departDate = new Date(departDateString)
  return differenceInDays(departDate, today)
}

export function getApGroup(searchRequest) {
  const departureDate = get(
    searchRequest.slices,
    [0, 'travelDateAsmm/dd/yyyy'],
    null
  )
  const daysOut = getNumDaysToDeparture(departureDate)
  if (daysOut <= 7) {
    return '0-7 days'
  } else if (daysOut <= 14) {
    return '8-14 days'
  } else if (daysOut <= 21) {
    return '15-21 days'
  } else if (daysOut <= 28) {
    return '22-28 days'
  }
  return '29+ days'
}

export function getCityCode(searchRequest, airportKey) {
  const airportCode = get(searchRequest, ['slices', 0, airportKey])
  return get(searchRequest, ['airports', airportCode, 'cityCode'])
}

export function findPriceGuidance(searchRequest) {
  const origin = getCityCode(searchRequest, 'originAirport')
  const destination = getCityCode(searchRequest, 'destinationAirport')
  const apGroup = getApGroup(searchRequest)
  const key = `${origin}-${destination}`
  return get(priceGuidanceTopDestinations, [key, apGroup], null)
}

export function isEconomy(searchRequest) {
  return get(searchRequest, 'params.cabin-class') === 'ECO'
}

export function getBackToFlightsLink(params, slicesUrl) {
  const queryParams = pick(params, [
    'cabin-class',
    'num-adults',
    'num-children',
    'num-infants',
    'num-youths',
  ])

  const backToSearchQueryParams = querystring.stringify(queryParams)

  return `/m/fly/search${slicesUrl}?${backToSearchQueryParams}`
}

export function getHasUpsellOptions(listings) {
  const upsellIndex = findIndex(
    listings,
    o => o.upsellOptions && o.upsellOptions.length > 0
  )
  return upsellIndex !== -1
}

const returnCallWithId = (cb, id) => () => {
  cb({ id })
}

export const buildInitialTabState = (tabs = [], defaultSelectedTab, cb) => {
  return tabs.map(tab => ({
    tabId: tab.id,
    title: tab.title,
    isActive: tab.id === defaultSelectedTab,
    callback: tab.isClickable && returnCallWithId(cb, tab.id),
  }))
}

export const buildInitialTabStateMD = (numSlices, defaultSelectedTab, cb) => {
  const tabs = []
  for (let i = 1; i <= numSlices; i++) {
    tabs.push({
      tabId: i,
      isActive: i === defaultSelectedTab,
      callback: returnCallWithId(cb, i),
    })
  }
  return tabs
}

export function generatePhoneBookInput(
  itineraryDetails,
  pclnBootstrapData,
  isMobile,
  isDomesticOnly,
  includeTestNumber = false
) {
  const formattedPurchaseWindow = moment(new Date())
    .format()
    .split('T')[0]

  const translatedRequest = translateSearchRequest(
    pclnBootstrapData?.searchRequest
  )

  const purchaseWindow = moment(translatedRequest?.travelDate).diff(
    formattedPurchaseWindow,
    'days'
  )

  const refId =
    pclnBootstrapData?.referral?.sourceId + pclnBootstrapData?.referral?.id

  const userCGUID = pclnBootstrapData?.webstats?.clientGUID

  const requestId = pclnBootstrapData?.webstats?.clientRequestId

  const airportsArray = Object.values(translatedRequest?.airports)

  const getLengthofStay = moment(translatedRequest?.returnDate).diff(
    translatedRequest?.travelDate,
    'days'
  )

  let marketingAirline = []
  let operatingAirline = []

  itineraryDetails?.slices?.forEach(sliceObj => {
    sliceObj?.segments?.forEach(segmentObj => {
      marketingAirline?.push(segmentObj?.marketingAirline)
      if (segmentObj?.operatingAirline) {
        operatingAirline?.push(segmentObj?.operatingAirline)
      }
    })
  })

  const phoneBookInput = {
    requestId: requestId,
    cguid: userCGUID,
    refid: refId,
    clientDevice: isMobile ? 'MOBILE' : 'DESKTOP',
    isTestNumber: includeTestNumber,
    advancedPurchaseWindow: purchaseWindow,
    currencyCode: 'USD',
    origCountryCode: airportsArray[0]?.countryCode,
    destCountryCode: airportsArray[1]?.countryCode,
    originAirport: translatedRequest?.departingFrom?.airportCode,
    clientCountryCode:
      pclnBootstrapData?.itinDetails?.pointOfSale?.requestSourceCountry,
    clientIp: pclnBootstrapData?.webstats?.resolvedClientIP,
    destinationAirport: translatedRequest?.returningFrom?.airportCode,
    isInternational: !isDomesticOnly,
    passengers: itineraryDetails?.numberInParty,
    travelStartDate: translatedRequest?.travelDate,
    travelEndDate: translatedRequest?.returnDate,
    searchOptions: {
      cabinClass: translatedRequest?.cabinClass,
    },
    pageType: 'DETAILS',
    tripType: translatedRequest?.tripType,
    marketingAirline: marketingAirline,
    operatingAirline: operatingAirline,
  }

  if (translatedRequest.tripType !== 'OW') {
    phoneBookInput.lengthOfStay = getLengthofStay
  }

  return phoneBookInput
}

export function generatePhoneBookPackagesInput(
  itineraryDetails,
  pclnBootstrapData,
  isMobile,
  airDetailsState,
  params
) {
  const translatedRequest = translateSearchRequest(
    pclnBootstrapData?.searchRequest
  )

  const {
    referral = {},
    partner = {},
    webstats = {},
    isDomestic = true,
  } = pclnBootstrapData

  const { sourceId = '', id = '', clickId = '' } = referral

  const refId = sourceId + id

  const userCGUID = webstats?.clientGUID

  const requestId = webstats?.clientRequestId

  const clientIP = webstats?.resolvedClientIP

  const airportsArray = Object.values(translatedRequest?.airports)

  const numChildren = parseInt(params['num-children'] || '0')

  const numAdults = parseInt(params['num-adults'] || '0')

  let marketingAirline = []
  let operatingAirline = []

  itineraryDetails?.slices.forEach(sliceObj => {
    // eslint-disable-next-line sonarjs/no-identical-functions
    sliceObj.segments.forEach(segmentObj => {
      marketingAirline.push(segmentObj?.marketingAirline)
      if (segmentObj?.operatingAirline) {
        operatingAirline.push(segmentObj.operatingAirline)
      }
    })
  })

  const placementID = 'Flight_Details'
  const clientDevice = isMobile ? 'MOBILE' : 'DESKTOP'

  const origin = {
    airportCode: translatedRequest?.departingFrom?.airportCode,
    cityID: airportsArray[0].cityId.toString(),
    country: itineraryDetails?.airport[0]?.country,
    countryCode: airportsArray[0]?.countryCode,
    type: 'AIRPORT',
  }

  const destination = {
    airportCode: translatedRequest?.returningFrom?.airportCode,
    cityID: airportsArray[1].cityId.toString(),
    country: itineraryDetails?.airport[1]?.country,
    countryCode: airportsArray[1]?.countryCode,
    type: 'AIRPORT',
  }

  return {
    cguid: userCGUID,
    clientDevice: clientDevice,
    placementID: placementID,
    clientIP: clientIP,
    pageURL: window.location.href,
    referral: {
      appCode: clientDevice,
      placementID: placementID,
      cguid: userCGUID,
      clientCountryCode: null,
      clientIP: clientIP,
      clientDevice: clientDevice,
      clientLocale: null,
      currencyCode: itineraryDetails?.price[0]?.currencyCode,
      globalPartnerCode: partner?.globalPartnerCode || 'PCLN',
      plfCode: partner?.plfCode || 'PCLN',
      referralClickID: clickId,
      referralSourceID: sourceId,
      referralID: id,
      refID: refId,
      requestID: requestId,
      userAgent: webstats?.userAgent,
      visitID: webstats?.visitId,
    },
    search: {
      packageSearchOptions: {
        adults: numAdults,
        children: numChildren,
        travelStartDate: translatedRequest?.travelDate,
        travelEndDate: translatedRequest?.returnDate,
        origin: origin,
        destination: destination,
        packagePrice: airDetailsState?.price[1]?.amount,
        packageType: null,
      },
      flightSearchOptions: {
        origin: origin,
        destination: destination,
        isInternational: !isDomestic,
        isSplitTicket: !(airDetailsState?.hasOnlyOneFareBrand || true),
        operatingAirlines: operatingAirline,
        marketingAirlines: marketingAirline,
      },
    },
  }
}
